import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ReportView from "../components/ReportView";
import "../css/LearnerDashboard.css";

const API = process.env.REACT_APP_API_BASE;

const LearnerDashboard = () => {
  const {
    fetchLearnerDashboard, learner, loading,
    submitCheckin, submitCheckout,
    fetchMyActivities, fetchMyAttendance, fetchNarrative,
    submitActivityWork, fetchReportData,
  } = useAuth();

  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [reportUrl, setReportUrl] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  /* checkin */
  const [mood, setMood] = useState("");
  const [journal, setJournal] = useState("");
  const [checkinMsg, setCheckinMsg] = useState("");
  const [checkedIn, setCheckedIn] = useState(false);

  /* activities */
  const [activities, setActivities] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityContent, setActivityContent] = useState("");
  const [submitMsg, setSubmitMsg] = useState("");

  /* attendance */
  const [attendance, setAttendance] = useState(null);

  /* narrative */
  const [narrative, setNarrative] = useState(null);

  /* report screen view */
  const [reportData, setReportData] = useState(null);

  /* tab loading */
  const [tabLoading, setTabLoading] = useState(false);

  useEffect(() => { fetchLearnerDashboard(); }, []);

  if (loading) return <div>Loading...</div>;

  const assessment = learner?.latest_assessment;
  const canTakeAssessment =
    !assessment ||
    (assessment?.next_assessment_date &&
      new Date() >= new Date(assessment.next_assessment_date));

  const handleGenerateReport = async () => {
    setReportLoading(true);
    try {
      const res = await fetch(`${API}/user/learner/reports/generate/assessment`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      if (data.file_path) {
        const url = `https://beintelli.engaggio.com${data.file_path}`;
        setReportUrl(url);
        window.open(url, "_blank");
      }
    } catch (err) {
      console.error("Report error:", err);
    } finally {
      setReportLoading(false);
    }
  };

  const nextAssessmentText = () => {
    if (!assessment?.next_assessment_date) return "Not Scheduled";
    if (canTakeAssessment) return "Available Now";
    return new Date(assessment.next_assessment_date).toLocaleString(undefined, {
      dateStyle: "medium", timeStyle: "short",
    });
  };

  const handleCheckin = async () => {
    if (!mood) return setCheckinMsg("Mood select చేయండి.");
    const ok = await submitCheckin({ mood, journal });
    if (ok) { setCheckinMsg("✅ Check-in complete!"); setCheckedIn(true); }
    else setCheckinMsg("❌ Check-in failed.");
  };

  const handleCheckout = async () => {
    const ok = await submitCheckout();
    setCheckinMsg(ok ? "✅ Check-out complete!" : "❌ Check-out failed.");
  };

  const handleTab = async (t) => {
    setTab(t);
    setTabLoading(true);
    try {
      if (t === "activities" && !activities) {
        const data = await fetchMyActivities();
        setActivities(Array.isArray(data) ? data : data?.activities || []);
      }
      if (t === "attendance" && !attendance) {
        const data = await fetchMyAttendance();
        setAttendance(data);
      }
      if (t === "narrative" && !narrative) {
        const data = await fetchNarrative();
        setNarrative(data);
      }
      if (t === "report" && !reportData) {
        const data = await fetchReportData();
        setReportData(data);
      }
    } finally {
      setTabLoading(false);
    }
  };

  const handleSubmitActivity = async () => {
    if (!activityContent.trim()) return setSubmitMsg("Content enter చేయండి.");
    const res = await submitActivityWork(selectedActivity.id, activityContent);
    if (res) { setSubmitMsg("✅ Submitted!"); setActivityContent(""); setSelectedActivity(null); }
    else setSubmitMsg("❌ Submit failed.");
  };

  const TABS = [
    { key: "overview",    label: "Overview" },
    { key: "checkin",     label: "Check-in" },
    { key: "activities",  label: "Activities" },
    { key: "attendance",  label: "Attendance" },
    { key: "narrative",   label: "AI Narrative" },
    { key: "report",      label: "My Report" },
  ];

  return (
    <div className="learner-dashboard">
      <header className="dashboard-header">
        <h1>Child Dashboard</h1>
        <p>Assessment Overview</p>
      </header>

      {/* TABS */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleTab(key)}
            style={{
              padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
              fontWeight: 600, fontSize: 13,
              background: tab === key ? "#1e293b" : "#f1f5f9",
              color: tab === key ? "#fff" : "#475569",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tabLoading && <div className="card">Loading...</div>}

      {/* OVERVIEW */}
      {tab === "overview" && !tabLoading && (
        <>
          <section className="card summary">
            <div>
              <h4>Last Assessment</h4>
              <p>
                {assessment?.date
                  ? new Date(assessment.date).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                  : "No assessment taken"}
              </p>
            </div>
            <div>
              <h4>Next Assessment</h4>
              <p>{nextAssessmentText()}</p>
            </div>
          </section>

          <section className="card highlight">
            <h2>Assessment</h2>
            <button
              className="btn"
              disabled={!canTakeAssessment}
              onClick={() => navigate("/child/assessment")}
            >
              {canTakeAssessment ? "Start Assessment" : "Not Available Yet"}
            </button>
          </section>

          {assessment && (
            <section className="card highlight">
              <h2>My Report</h2>
              <p style={{ marginBottom: "12px", color: "#64748b", fontSize: "13px" }}>
                Latest assessment report with AI-generated insights.
              </p>
              <button className="btn" onClick={handleGenerateReport} disabled={reportLoading}>
                {reportLoading ? "Generating..." : "Download Report PDF"}
              </button>
              {reportUrl && (
                <p style={{ marginTop: "10px", fontSize: "12px" }}>
                  <a href={reportUrl} target="_blank" rel="noreferrer">Open Report</a>
                </p>
              )}
            </section>
          )}
        </>
      )}

      {/* CHECK-IN */}
      {tab === "checkin" && !tabLoading && (
        <section className="card">
          <h2>Daily Check-in</h2>
          <p style={s.hint}>How are you feeling today?</p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            {["happy", "good", "okay", "sad", "tired"].map((m) => (
              <button
                key={m}
                onClick={() => setMood(m)}
                style={{
                  padding: "8px 16px", borderRadius: 20, border: "2px solid",
                  borderColor: mood === m ? "#6366f1" : "#e2e8f0",
                  background: mood === m ? "#eef2ff" : "#fff",
                  fontWeight: 600, fontSize: 13, cursor: "pointer", textTransform: "capitalize",
                }}
              >
                {m}
              </button>
            ))}
          </div>

          <textarea
            style={{ width: "100%", minHeight: 80, padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, resize: "vertical", boxSizing: "border-box" }}
            placeholder="Anything to share? (optional)"
            value={journal}
            onChange={(e) => setJournal(e.target.value)}
          />

          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <button style={s.btn} onClick={handleCheckin} disabled={checkedIn}>
              {checkedIn ? "Checked In ✓" : "Check In"}
            </button>
            <button style={{ ...s.btn, background: "#475569" }} onClick={handleCheckout}>
              Check Out
            </button>
          </div>
          {checkinMsg && <p style={{ marginTop: 10, fontSize: 13 }}>{checkinMsg}</p>}
        </section>
      )}

      {/* ACTIVITIES */}
      {tab === "activities" && !tabLoading && (
        <section className="card">
          <h2>My Activities</h2>
          {selectedActivity && (
            <>
              <h3 style={{ marginBottom: 8 }}>{selectedActivity.title}</h3>
              <p style={s.hint}>{selectedActivity.description}</p>
              <textarea
                style={{ width: "100%", minHeight: 100, padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, resize: "vertical", boxSizing: "border-box" }}
                placeholder="Your answer / submission..."
                value={activityContent}
                onChange={(e) => setActivityContent(e.target.value)}
              />
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button style={s.btn} onClick={handleSubmitActivity}>Submit</button>
                <button style={{ ...s.btn, background: "#94a3b8" }} onClick={() => setSelectedActivity(null)}>Cancel</button>
              </div>
              {submitMsg && <p style={{ marginTop: 8, fontSize: 13 }}>{submitMsg}</p>}
            </>
          )}
          {!selectedActivity && activities?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {activities.map((a) => (
                <div key={a.id} style={s.actCard}>
                  <div>
                    <strong>{a.title || a.activity_title}</strong>
                    <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>{a.description || ""}</p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>{a.status || "pending"}</span>
                    {(!a.status || a.status === "pending") && (
                      <button style={{ ...s.btn, padding: "6px 14px", fontSize: 12 }} onClick={() => setSelectedActivity(a)}>
                        Submit
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!selectedActivity && !activities?.length && (
            <p style={{ color: "#94a3b8" }}>No activities assigned yet.</p>
          )}
        </section>
      )}

      {/* ATTENDANCE */}
      {tab === "attendance" && !tabLoading && (
        <section className="card">
          <h2>My Attendance</h2>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 16 }}>
            <div style={s.statBox}><span style={s.statNum}>{attendance?.present ?? 0}</span><span style={s.statLabel}>Present</span></div>
            <div style={s.statBox}><span style={s.statNum}>{attendance?.absent ?? 0}</span><span style={s.statLabel}>Absent</span></div>
            <div style={s.statBox}><span style={s.statNum}>{attendance?.total ?? 0}</span><span style={s.statLabel}>Total Days</span></div>
          </div>
          {attendance?.records?.length ? (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Date</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Check-in</th>
                  <th style={s.th}>Check-out</th>
                </tr>
              </thead>
              <tbody>
                {attendance.records.map((r) => (
                  <tr key={r.id || r.date} style={s.tr}>
                    <td style={s.td}>{new Date(r.date).toDateString()}</td>
                    <td style={s.td}>{r.status}</td>
                    <td style={s.td}>{r.checkin_time || "—"}</td>
                    <td style={s.td}>{r.checkout_time || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ color: "#94a3b8" }}>No attendance records.</p>
          )}
        </section>
      )}

      {/* AI NARRATIVE */}
      {tab === "narrative" && !tabLoading && (
        <section className="card">
          <h2>AI Narrative</h2>
          {narrative?.narrative ? (
            <div style={{ lineHeight: 1.7, fontSize: 15, color: "#1e293b", whiteSpace: "pre-wrap" }}>
              {narrative.narrative}
            </div>
          ) : (
            <p style={{ color: "#94a3b8" }}>No narrative available yet. Complete an assessment first.</p>
          )}
        </section>
      )}

      {/* MY REPORT */}
      {tab === "report" && !tabLoading && (
        reportData
          ? <ReportView
              data={reportData}
              downloadLoading={reportLoading}
              onDownload={handleGenerateReport}
            />
          : <section className="card">
              <p style={{ color: "#94a3b8" }}>No report available yet. Complete an assessment first.</p>
            </section>
      )}
    </div>
  );
};

const s = {
  hint:     { fontSize: 13, color: "#64748b", marginBottom: 12 },
  btn:      { padding: "9px 20px", background: "#1e293b", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  table:    { width: "100%", borderCollapse: "collapse" },
  th:       { textAlign: "left", padding: "10px 12px", fontSize: 12, fontWeight: 700, color: "#64748b", borderBottom: "1px solid #e2e8f0", textTransform: "uppercase" },
  tr:       { borderBottom: "1px solid #f1f5f9" },
  td:       { padding: "12px 12px", fontSize: 14, color: "#1e293b" },
  statBox:  { display: "flex", flexDirection: "column", alignItems: "center", background: "#f8fafc", borderRadius: 12, padding: "16px 24px", minWidth: 80 },
  statNum:  { fontSize: 28, fontWeight: 800, color: "#1e293b" },
  statLabel:{ fontSize: 12, color: "#64748b", marginTop: 4 },
  actCard:  { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 16px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" },
};

export default LearnerDashboard;
